import {
    canAddAtDepth,
    canAddGroupAtDepth,
    createAdvancedFilterEgenskapNode,
    createAdvancedFilterGroup,
    createAdvancedFilterOverlappNode,
    createAdvancedFilterRelasjonNode,
} from '@/domain/advancedFilter';

export type AddActionKind = 'egenskap' | 'overlapp' | 'relasjon' | 'group';

interface AddButtonsProps {
    onAdd: (kind: AddActionKind) => void;
    containerDepth: number;
}

export const AddButtons: React.FC<AddButtonsProps> = ({ onAdd, containerDepth }) => {
    const canAddFilter = canAddAtDepth(containerDepth);
    const canAddGroup = canAddGroupAtDepth(containerDepth);
    const filterDisabledReason = `Kan ikke legge til flere filter fordi maksimal filtere inni hverandre er nådd.`;
    const groupDisabledReason =
        'Kan ikke legge til underfilter fordi det må være plass til innhold inni underfilteret innenfor maksimal dybde.';

    return (
        <div className="c-advanced-filter__add-buttons">
            <span className="c-advanced-filter__add-prefix">Legg til:</span>
            <AddActionButton
                label="Egenskap"
                kind="egenskap"
                title="Filtrer vegobjekttypen på egenskap"
                onAdd={onAdd}
                disabled={!canAddFilter}
                disabledReason={filterDisabledReason}
            />
            <AddActionButton
                label="Overlapp"
                kind="overlapp"
                title="Filtrer på overlappende stedfesting for vegobjekter av en annen gitt type"
                onAdd={onAdd}
                disabled={!canAddFilter}
                disabledReason={filterDisabledReason}
            />
            <AddActionButton
                label="Relasjon"
                kind="relasjon"
                title="Filtrer på relasjon (foreldre/barn) for vegobjekter av en annen gitt type"
                onAdd={onAdd}
                disabled={!canAddFilter}
                disabledReason={filterDisabledReason}
            />
            <AddActionButton
                label="Underfilter"
                kind="group"
                title="Legg til et underfilter som samler flere filtere sammen med en logisk operator og/eller"
                onAdd={onAdd}
                disabled={!canAddGroup}
                disabledReason={groupDisabledReason}
            />
        </div>
    );
};

interface AddActionButtonProps {
    label: string;
    kind: AddActionKind;
    title: string;
    onAdd: (kind: AddActionKind) => void;
    disabled: boolean;
    disabledReason: string;
}

const AddActionButton: React.FC<AddActionButtonProps> = ({
    label,
    kind,
    title,
    onAdd,
    disabled,
    disabledReason,
}) => {
    const button = (
        <button
            type="button"
            className={`c-advanced-filter__add-button c-advanced-filter__add-button--${kind}`}
            onClick={() => onAdd(kind)}
            disabled={disabled}
            title={disabled ? disabledReason : title}
        >
            <span className="c-advanced-filter__add-button-text">{label}</span>
        </button>
    );

    return button;
};

export function createNodeForAddAction(kind: AddActionKind) {
    switch (kind) {
        case 'egenskap':
            return createAdvancedFilterEgenskapNode();
        case 'overlapp':
            return createAdvancedFilterOverlappNode();
        case 'relasjon':
            return createAdvancedFilterRelasjonNode();
        case 'group':
            return createAdvancedFilterGroup();
    }
}
